import { capitalizeFirstLetter } from "helpers/text";
import { buildURLforOrders } from "pages/Orders/helpers/helpers";
import {
  IExtendedOrder,
  IOrderCreateRequest,
  IOrderCancelRequest,
  IOrdersRequest,
  IMarkPaidCashUnmanagedPaymentRecord,
} from "types/Order/Order";
import {
  IOrdersResponse,
  ISingleOrderResponse,
  ISingleOrderResumeResponse,
} from "types/Order/OrderResponse";
import { ICreatePaymentRequest } from "types/Payment";

import { publicApi, api } from ".";

const orderQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<IOrdersResponse, IOrdersRequest>({
      query: ({
        accountId,
        page,
        pageSize,
        status,
        orderType,
        patientName,
        dateOfService,
        sortOrder,
        sortField,
        changedByEligibilityRerun,
      }) =>
        buildURLforOrders({
          accountId,
          page,
          pageSize,
          status,
          orderType,
          patientName,
          dateOfService,
          sortOrder,
          sortField,
          changedByEligibilityRerun,
        }),
    }),
    getOrder: builder.query<ISingleOrderResponse, string | undefined>({
      query: (orderId) => `/orders/${orderId}`,
    }),
    createOrders: builder.mutation<ISingleOrderResponse, IOrderCreateRequest>({
      query: (newOrder) => ({
        url: "orders",
        method: "POST",
        body: newOrder,
      }),
    }),
    cancelSingleOrder: builder.mutation<
      ISingleOrderResponse,
      IOrderCancelRequest
    >({
      query: ({ orderId, note }: { orderId: string; note?: string }) => ({
        url: `orders/${orderId}/cancel`,
        method: "PATCH",
        body: {
          note,
        },
      }),
    }),
    updateOrder: builder.mutation<IOrdersResponse, Partial<IExtendedOrder>>({
      query: (orderToUpdate) => ({
        method: "PATCH",
        url: `/orders/${orderToUpdate.id}`,
        body: {
          amountInCents: orderToUpdate.amountInCents,
          orderType: orderToUpdate.orderType,
          integrationErrors: orderToUpdate.integrationErrors,
          ...(orderToUpdate.orderAttemptNumber !== undefined
            ? { orderAttemptNumber: orderToUpdate.orderAttemptNumber }
            : {}),
          externalId: orderToUpdate.externalId,
          serviceDetails: orderToUpdate.serviceDetails,
          accountNumber: orderToUpdate.accountNumber,
          dateOfService: orderToUpdate.dateOfService,
          status: orderToUpdate?.status,
          gfeQuoteId: orderToUpdate.gfeQuoteId,
          patientId: orderToUpdate.patientId,
          patient: {
            firstName: orderToUpdate.patient?.firstName,
            lastName: orderToUpdate.patient?.lastName,
            dateOfBirth: orderToUpdate.patient?.dateOfBirth,
            preferredContactMethod:
              orderToUpdate.patient?.preferredContactMethod,
            phoneNumber: orderToUpdate.patient?.phoneNumber,
            updatedAt: new Date(),
            patientBenefits: {
              isSubscriberPatient:
                orderToUpdate.patient?.patientBenefits.isSubscriberPatient,
              memberID: orderToUpdate.patient?.patientBenefits.memberID,
            },
          },
          paymentPlanDuration: orderToUpdate?.paymentPlanDuration,
          paymentPlanMinDownPaymentPercent:
            orderToUpdate?.paymentPlanMinDownPaymentPercent,
          paymentPlan: orderToUpdate?.paymentPlan,
          outreachStopped: orderToUpdate?.outreachStopped,
          providerPayout: orderToUpdate?.providerPayout,
          scheduledFirstCommunicationDate:
            orderToUpdate.scheduledFirstCommunicationDate,
          installmentFrequency: orderToUpdate?.installmentFrequency,
          installmentAmountInCents: orderToUpdate?.installmentAmountInCents,
          calculationBasis: orderToUpdate?.calculationBasis,
          accountLocationId: orderToUpdate.accountLocationId,
        },
      }),
    }),
    redoOrder: builder.mutation<ISingleOrderResponse, IOrderCreateRequest>({
      query: (orderToUpdate) => ({
        method: "PATCH",
        url: `/orders/redo/${orderToUpdate.orderId}`,
        body: orderToUpdate,
      }),
    }),
    saveDraft: builder.mutation<IOrdersResponse, Partial<IExtendedOrder>>({
      query: (orderToUpdate) => ({
        method: "PATCH",
        url: `/orders/draft/${orderToUpdate.id}`,
        body: {
          amountInCents: orderToUpdate.amountInCents,
          orderType: orderToUpdate.orderType,
          integrationErrors: orderToUpdate.integrationErrors,
          orderAttemptNumber: 0,
          externalId: orderToUpdate.externalId,
          payerId: orderToUpdate.payerId,
          secondaryPayerId: orderToUpdate.secondaryPayerId,
          serviceDetails: orderToUpdate.serviceDetails,
          accountNumber: orderToUpdate.accountNumber,
          dateOfService: orderToUpdate.dateOfService,
          status: orderToUpdate?.status,
          gfeQuoteId: orderToUpdate.gfeQuoteId,
          patientId: orderToUpdate.patientId,
          patient: {
            firstName: orderToUpdate.patient?.firstName,
            lastName: orderToUpdate.patient?.lastName,
            email: orderToUpdate.patient?.email,
            dateOfBirth: orderToUpdate.patient?.dateOfBirth,
            preferredContactMethod:
              orderToUpdate.patient?.preferredContactMethod,
            phoneNumber: orderToUpdate.patient?.phoneNumber,
            updatedAt: new Date(),
            patientBenefits: {
              isSubscriberPatient:
                orderToUpdate.patient?.patientBenefits.isSubscriberPatient,
              memberID: orderToUpdate.patient?.patientBenefits.memberID,
            },
            secondaryPatientBenefits:
              orderToUpdate?.patient?.secondaryPatientBenefits,
          },
          paymentPlanDuration: orderToUpdate?.paymentPlanDuration,
          paymentPlanMinDownPaymentPercent:
            orderToUpdate?.paymentPlanMinDownPaymentPercent,
          paymentPlanEnabled: orderToUpdate?.paymentPlanEnabled,
          paymentPlanMinDownPaymentFlat:
            orderToUpdate?.paymentPlanMinDownPaymentFlat,
          providerDistributionLogic: orderToUpdate?.providerDistributionLogic,
          isDownPaymentOnly: orderToUpdate?.isDownPaymentOnly || false,
          paymentPlanCalculationBasis:
            orderToUpdate?.paymentPlanCalculationBasis,
          installmentFrequency: orderToUpdate?.installmentFrequency,
          installmentAmountInCents: orderToUpdate?.installmentAmountInCents,
          benefitDetails: orderToUpdate?.benefitDetails,
          secondaryBenefitDetails: orderToUpdate?.secondaryBenefitDetails,
          outreachStopped: orderToUpdate.outreachStopped,
          patientBenefits: orderToUpdate.patientBenefits,
          secondaryPatientBenefits: orderToUpdate.secondaryPatientBenefits,
          secondaryPverifyResponseId: orderToUpdate.secondaryPverifyResponseId,
          scheduledFirstCommunicationDate:
            orderToUpdate.scheduledFirstCommunicationDate,
          scheduledFirstCommunicationSent:
            orderToUpdate.scheduledFirstCommunicationSent,
          scheduledCommunicationCustomText:
            orderToUpdate.scheduledCommunicationCustomText,
          accountLocationId: orderToUpdate.accountLocationId,
          excludedNcciRulesetIds: orderToUpdate.excludedNcciRulesetIds || [],
          excludedNcciInfo: orderToUpdate.excludedNcciInfo || [],
        },
      }),
    }),
    approveDraftOrder: builder.mutation<
      ISingleOrderResponse,
      IOrderCreateRequest
    >({
      query: (orderToUpdate) => ({
        method: "PATCH",
        url: `/orders/approve/${orderToUpdate.orderId}`,
        body: orderToUpdate,
      }),
    }),
    markPaidViaCashUnmanaged: builder.mutation<
      ISingleOrderResponse,
      {
        orderId: string;
        note?: string;
        captchaToken?: string;
        directPayLinkId?: string;
        paymentRecords?: IMarkPaidCashUnmanagedPaymentRecord[];
        isDirectPay?: boolean;
      }
    >({
      query: ({
        orderId,
        note,
        captchaToken,
        directPayLinkId,
        paymentRecords,
        isDirectPay,
      }) => ({
        method: "POST",
        url: `/orders/${orderId}/mark-paid-cash-unmanaged`,
        body: {
          note,
          ...(captchaToken && { captchaToken }),
          ...(directPayLinkId && { directPayLinkId }),
          ...(paymentRecords && { paymentRecords }),
          ...(isDirectPay && { isDirectPay }),
        },
      }),
    }),
  }),
});

const publicOrderQueries = publicApi.injectEndpoints({
  endpoints: (builder) => ({
    createPayment: builder.mutation<any, ICreatePaymentRequest>({
      query: (payment) => ({
        body: {
          amountInCents: payment.amountInCents,
          paymentType: capitalizeFirstLetter(payment.paymentType),
          stripeBalanceTransactionId: payment.stripeBalanceTransactionId,
          userId: payment.userId,
          providerId: payment.providerId,
          paymentPlan: payment.paymentPlan,
          paymentMethodId: payment.paymentMethodId,
          note: payment.note,
          payRemainingPrice: payment.payRemainingPrice,
          savePaymentMethod: payment.savePaymentMethod,
          termsAcceptance: payment.termsAcceptance,
        },
        method: "POST",
        url: `orders/${payment.orderId}/payment`,
      }),
    }),
    getSavedPaymentMethod: builder.query({
      query: (orderId) => ({
        url: `orders/${orderId}/saved_payment_method`,
        method: "GET",
      }),
    }),
    createOrderPaymentIntent: builder.mutation({
      query: ({
        orderId,
        paymentPlan,
        payRemainingPrice,
        customAmount,
        savePaymentMethod,
        useSavedPaymentMethod,
      }: {
        orderId: string;
        paymentPlan: boolean;
        payRemainingPrice: boolean;
        customAmount?: number;
        savePaymentMethod?: boolean;
        useSavedPaymentMethod?: boolean;
      }) => ({
        url: `orders/${orderId}/create_payment_intent`,
        method: "POST",
        body: {
          paymentPlan,
          payRemainingPrice,
          customAmount,
          savePaymentMethod,
          useSavedPaymentMethod,
        },
      }),
    }),
    createSplitPaymentIntent: builder.mutation({
      query: ({
        orderId,
        amounts,
        paymentPlan,
        payRemainingPrice,
        manualCapture = true,
      }: {
        orderId: string;
        amounts: number[];
        paymentPlan: boolean;
        payRemainingPrice: boolean;
        manualCapture: boolean;
      }) => ({
        url: `orders/${orderId}/create_split_payment_intent`,
        method: "POST",
        body: {
          amounts,
          paymentPlan,
          payRemainingPrice,
          manualCapture,
        },
      }),
    }),
    captureSplitPayment: builder.mutation({
      query: ({
        orderId,
        paymentIntentIds,
        amounts,
        paymentMethodIds,
        paymentTypes,
        userId,
        paymentPlan,
        preferredPaymentMethodIndex,
        termsAcceptance,
      }) => ({
        url: `orders/${orderId}/capture_split_payment`,
        method: "POST",
        body: {
          paymentIntentIds,
          amounts,
          paymentMethodIds,
          paymentTypes: paymentTypes.map((type: string) =>
            capitalizeFirstLetter(type)
          ),
          userId,
          paymentPlan,
          preferredPaymentMethodIndex,
          termsAcceptance,
        },
      }),
    }),
    cancelPaymentIntents: builder.mutation({
      query: ({
        orderId,
        paymentIntentIds,
        failureReason,
        userId,
      }: {
        orderId: string;
        paymentIntentIds: string[];
        failureReason: string;
        userId?: string;
      }) => ({
        url: `orders/${orderId}/cancel_payment_intents`,
        method: "POST",
        body: {
          paymentIntentIds,
          failureReason,
          userId,
        },
      }),
    }),
    createOrderSetupIntent: builder.mutation({
      query: ({ orderId }) => ({
        url: `orders/${orderId}/create_setup_intent`,
        method: "POST",
        body: {},
      }),
    }),
    getOrderOverview: builder.query<
      ISingleOrderResumeResponse,
      string | undefined
    >({
      query: (orderId) => `/orders/${orderId}/overview`,
    }),
    getPublicOrder: builder.query<ISingleOrderResponse, string | undefined>({
      query: (orderId) => `/orders/${orderId}/public`,
    }),
    updateNextInstallmentDate: builder.mutation({
      query: ({
        orderId,
        nextInstallmentDate,
        userId,
      }: {
        orderId: string;
        nextInstallmentDate: string;
        userId?: string | null;
      }) => ({
        url: `orders/${orderId}/change_next_installment_date`,
        method: "PATCH",
        body: { nextInstallmentDate, userId },
      }),
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderQuery,
  useCreateOrdersMutation,
  useCancelSingleOrderMutation,
  useUpdateOrderMutation,
  useApproveDraftOrderMutation,
  useSaveDraftMutation,
  useRedoOrderMutation,
  useMarkPaidViaCashUnmanagedMutation,
} = orderQueries;

export const {
  useCreatePaymentMutation,
  useCreateOrderPaymentIntentMutation,
  useCreateSplitPaymentIntentMutation,
  useCaptureSplitPaymentMutation,
  useCancelPaymentIntentsMutation,
  useCreateOrderSetupIntentMutation,
  useGetOrderOverviewQuery,
  useGetPublicOrderQuery,
  useGetSavedPaymentMethodQuery,
  useUpdateNextInstallmentDateMutation,
} = publicOrderQueries;
